import React from 'react';
import { Phone, Mail, MapPin, Facebook, Instagram, Twitter, Youtube, ArrowRight, Heart, Linkedin } from 'lucide-react';

const Footer = () => {
  const quickLinks = [
    { name: 'About Us', href: '#about' },
    { name: 'Services', href: '#services' },
    { name: 'Corporate Wellness', href: '#corporate' },
    { name: 'Resources', href: '#resources' },
    { name: 'Contact', href: '#contact' }
  ];

  const services = [
    'Sports Physiotherapy',
    'Orthopedic Rehabilitation',
    'Neurological Physiotherapy',
    'Pediatric Care',
    'Geriatric Care',
    'Corporate Wellness',
    'Home Visits',
    'Telehealth Consultations'
  ];

  const socialLinks = [
    { icon: Linkedin, href: 'https://www.linkedin.com/company/arpan-physiocure', color: 'hover:text-blue-400', label: 'LinkedIn Company' },
    { icon: Instagram, href: 'https://www.instagram.com/arpan.physiocure/', color: 'hover:text-pink-400', label: 'Instagram' },
    { icon: Linkedin, href: 'https://www.linkedin.com/in/dr-deep-maheshwari-pt-4b369a196', color: 'hover:text-blue-400', label: 'Dr. Deep LinkedIn' },
    { icon: Youtube, href: 'https://www.youtube.com/channel/UCygeKSUZOxh2vJUbH3QDzYw', color: 'hover:text-red-400', label: 'YouTube' }
  ];

  return (
    <footer className="bg-gray-900 text-white">
      {/* Newsletter Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h3 className="text-xl sm:text-2xl font-bold mb-4">Stay Updated with Health Tips</h3>
            <p className="text-sm sm:text-base text-blue-100 mb-6 sm:mb-8">
              Get expert physiotherapy advice and wellness tips delivered to your inbox
            </p>
            <div className="max-w-md mx-auto flex flex-col sm:flex-row gap-2 sm:gap-0">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-2 sm:py-3 rounded-full sm:rounded-l-full sm:rounded-r-none text-gray-900 focus:outline-none focus:ring-2 focus:ring-white text-sm sm:text-base"
                aria-label="Email address for newsletter subscription"
              />
              <button className="bg-white text-blue-600 hover:bg-gray-100 px-4 sm:px-6 py-2 sm:py-3 rounded-full sm:rounded-l-none sm:rounded-r-full font-semibold transition-colors duration-200 flex items-center justify-center text-sm sm:text-base shadow-lg hover:shadow-xl">
                Subscribe
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="lg:col-span-1">
              <div className="mb-6">
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">
                  Arpan PhysioCure™
                </h2>
                <p className="text-sm sm:text-base text-gray-400">Premium Physiotherapy Care</p>
              </div>
              <p className="text-sm sm:text-base text-gray-400 mb-6">
                Leading physiotherapy clinic in Indore, providing comprehensive rehabilitation 
                and wellness services with a patient-centered approach.
              </p>
              
              {/* Contact Info */}
              <div className="space-y-3">
                <div className="flex items-center">
                  <Phone className="w-5 h-5 text-blue-400 mr-3" />
                  <a 
                    href="tel:+917974434671"
                    className="text-sm sm:text-base text-gray-300 hover:text-white transition-colors duration-200"
                  >
                    +91 7974434671
                  </a>
                </div>
                <div className="flex items-center">
                  <Mail className="w-5 h-5 text-blue-400 mr-3" />
                  <a 
                    href="mailto:arpan.physiocure@gmail.com"
                    className="text-sm sm:text-base text-gray-300 hover:text-white transition-colors duration-200"
                  >
                    arpan.physiocure@gmail.com
                  </a>
                </div>
                <div className="flex items-start">
                  <MapPin className="w-5 h-5 text-blue-400 mr-3 mt-1" />
                  <span className="text-sm sm:text-base text-gray-300">
                    55 - Ambika puri Ext., 60 feet Road<br />
                    Near Darsh Hospital / Royal Enfield<br />
                    Indore - 452005
                  </span>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-base sm:text-lg font-semibold mb-4 sm:mb-6">Quick Links</h3>
              <ul className="space-y-3">
                {quickLinks.map((link, index) => (
                  <li key={index}>
                    <a
                      href={link.href}
                      className="text-sm sm:text-base text-gray-400 hover:text-white hover:underline transition-colors duration-200"
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
                <li>
                  <a
                    href="#"
                    className="text-sm sm:text-base text-gray-400 hover:text-white hover:underline transition-colors duration-200"
                  >
                    Pricing & Insurance
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="text-sm sm:text-base text-gray-400 hover:text-white hover:underline transition-colors duration-200"
                  >
                    Patient Portal
                  </a>
                </li>
              </ul>
            </div>

            {/* Services */}
            <div>
              <h3 className="text-base sm:text-lg font-semibold mb-4 sm:mb-6">Our Services</h3>
              <ul className="space-y-3">
                {services.slice(0, 6).map((service, index) => (
                  <li key={index}>
                    <a
                      href="#services"
                      className="text-sm sm:text-base text-gray-400 hover:text-white hover:underline transition-colors duration-200"
                    >
                      {service}
                    </a>
                  </li>
                ))}
                <li>
                  <a
                    href="#services"
                    className="text-sm sm:text-base text-blue-400 hover:text-blue-300 font-semibold transition-colors duration-200"
                  >
                    View All Services →
                  </a>
                </li>
              </ul>
            </div>

            {/* Emergency & Hours */}
            <div>
              <h3 className="text-base sm:text-lg font-semibold mb-4 sm:mb-6">Clinic Hours</h3>
              <div className="space-y-3 text-gray-400">
                <div>
                  <p className="font-medium text-white text-sm sm:text-base">Regular Hours</p>
                  <p className="text-sm sm:text-base">Monday - Saturday</p>
                  <p className="text-sm sm:text-base">9:00 AM - 1:00 PM</p>
                  <p className="text-sm sm:text-base">5:00 PM - 9:00 PM</p>
                </div>
                <div>
                  <p className="font-medium text-white text-sm sm:text-base">Sunday</p>
                  <p className="text-sm sm:text-base">10:00 AM - 12:00 PM</p>
                </div>
                <div className="bg-red-600 p-3 sm:p-4 rounded-lg mt-4 sm:mt-6">
                  <p className="font-semibold text-white mb-2 text-sm sm:text-base">Emergency Care</p>
                  <p className="text-red-100 text-sm">24/7 Available</p>
                  <a 
                    href="https://wa.me/917974434671"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white text-red-600 hover:bg-gray-100 px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-semibold mt-3 transition-colors duration-200 inline-block shadow-lg hover:shadow-xl"
                    aria-label="WhatsApp emergency number"
                  >
                    WhatsApp Now
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Social Links & Certifications */}
      <div className="border-t border-gray-800 py-6 sm:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center">
            <div className="flex space-x-4 sm:space-x-6 mb-4 sm:mb-0">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`text-gray-400 ${social.color} transition-colors duration-200 p-2 rounded-full hover:bg-gray-800`}
                  aria-label={social.label}
                >
                  <social.icon className="w-6 h-6" />
                </a>
              ))}
            </div>
            
            <div className="flex items-center space-x-3 sm:space-x-6 text-gray-400 text-xs sm:text-sm">
              <span>AIFF Certified</span>
              <span>AITA Approved</span>
              <span className="hidden sm:inline">Licensed Practitioners</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center text-xs sm:text-sm text-gray-400">
            <div className="flex items-center mb-4 sm:mb-0 text-center sm:text-left">
              <span>© 2024 Arpan PhysioCure™. All rights reserved.</span>
              <span className="mx-2 hidden sm:inline">•</span>
              <span className="flex items-center">
                <span className="hidden sm:inline">Made with</span>
                <Heart className="w-4 h-4 text-red-500 mx-1" />
                <span className="hidden sm:inline">in Indore</span>
              </span>
            </div>
            
            <div className="flex flex-wrap justify-center gap-3 sm:gap-6">
              <a href="#" className="hover:text-white transition-colors duration-200">
                Privacy Policy
              </a>
              <a href="#" className="hover:text-white transition-colors duration-200">
                Terms of Service
              </a>
              <a href="#" className="hover:text-white transition-colors duration-200">
                Cookie Policy
              </a>
              <a href="#" className="hover:text-white transition-colors duration-200">
                Accessibility
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;